import {
  activateCustomerWallet,
  getCustomerWalletDetails,
  sendCustomerWalletDetailsBySMS
} from "api";
import {
  CustomerWalletStatus,
  ICustomerWalletDetails,
  ILoggedCustomer,
  ITabletData
} from "api/types";
import { ApolloClient } from "apollo-boost";
import Divider from "components/common/Divider";
import IconicField from "components/common/IconicField";
import PanelLayout from "components/common/PanelLayout/PanelLayout";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { TabletDataContext } from "components/Gates/TabletDataGate";
import { shouldHideUsageType } from "components/UsageTypeSelector/utils";
import { History } from "history";
import * as React from "react";
import { ApolloConsumer } from "react-apollo";
import { Col, Container, Row } from "react-grid-system";

import delay from "utils/delay";
import EditProfileTabs, { EditProfileTabNames } from "../tabs/EditProfileTabs";
import ActivateBlockchainWallet from "./ActivateBlockchainWallet";
import BlockchainWalletDetails from "./BlockchainWalletDetails";

interface IProps {
  client: ApolloClient<any>;
  history: History;
  hasNoInterests?: boolean;
  loggedCustomer: ILoggedCustomer;
  tabletData: ITabletData;
  activateBlockchainWallet(): Promise<ICustomerWalletDetails | undefined>;
}

interface IState {
  isMessageSent?: boolean;
  tenantId: string;
  loading: boolean;
  activating: boolean;
  sending: boolean;
  error: boolean;
  errorMessage: string;
  walletDetails?: ICustomerWalletDetails;
}

class BlockchainProfile extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      tenantId: "",
      // TODO: Revise, maybe the same loading prop can be used instead of loading, activating and sending since they represent linear steps
      loading: true,
      activating: false,
      sending: false,
      error: false,
      errorMessage: "",
      isMessageSent: false
    };
  }

  public async componentDidMount() {
    await this.loadCustomerWalletDetails();
  }

  public render() {
    const { hasNoInterests, history } = this.props;
    return (
      <PanelLayout title="Blockchain Profile" closeButtonHandler>
        <div style={{ width: "100%", display: "grid" }}>
          <EditProfileTabs
            hideUsageTypeTab={shouldHideUsageType(this.props.tabletData)}
            hasNoInterests={hasNoInterests}
            walletStatus={CustomerWalletStatus.WalletActivated}
            history={history}
            currentTab={EditProfileTabNames.Blockchain}
          />
          <Container>
            <Row align="center" justify="center">
              <Col xs={12}>{this.renderProfile()}</Col>
              <Divider transparent />
            </Row>
          </Container>
        </div>
      </PanelLayout>
    );
  }

  private renderProfile = (): React.ReactNode => {
    const {
      loading,
      walletDetails,
      isMessageSent,
      errorMessage,
      activating,
      sending,
      error
    } = this.state;
    if (loading) {
      return <IconicField loading={loading} />;
    } else if (walletDetails) {
      return (
        <BlockchainWalletDetails
          errorMessage={errorMessage}
          error={error}
          sending={sending}
          walletDetails={walletDetails}
          sendCustomerWalletDetailsBySMS={
            this.handleSendCustomerWalletDetailsBySMS
          }
          isMessageSent={isMessageSent}
        />
      );
    } else {
      return (
        <ActivateBlockchainWallet
          activateBlockchainWallet={this.handleBlockchainWalletActivation}
          activating={activating}
        />
      );
    }
  };

  private loadCustomerWalletDetails = async () => {
    const { client, loggedCustomer } = this.props;
    const blockchainData = await getCustomerWalletDetails(
      client,
      loggedCustomer.authToken
    );
    this.setState({ walletDetails: blockchainData.walletDetails });
    // TODO: Delay to test loading interaction. Remove when calls to stellar network are implemented
    await delay(400);
    this.setState({ loading: false });
  };

  private handleSendCustomerWalletDetailsBySMS = async () => {
    const { client, loggedCustomer } = this.props;
    this.setState({ sending: true, error: false });
    await delay(700);
    try {
      await sendCustomerWalletDetailsBySMS(client, loggedCustomer.authToken);
      this.setState({ isMessageSent: true });
    } catch (error) {
      this.setState({
        error: true,
        errorMessage: "Failed to send SMS message"
      });
      console.error(error.message);
    } finally {
      this.setState({ sending: false });
    }
  };

  private handleBlockchainWalletActivation = async () => {
    const { client, loggedCustomer } = this.props;
    this.setState({ activating: true });
    try {
      const walletDetails = await activateCustomerWallet(
        client,
        loggedCustomer.authToken
      );
      this.setState({ walletDetails });
    } catch (error) {
      console.error(error.message);
    } finally {
      this.setState({ activating: false });
    }
  };
}

export default (props: IProps) => (
  <ApolloConsumer>
    {client => (
      <CustomerDataContext.Consumer>
        {loggedCustomer =>
          loggedCustomer && (
            <TabletDataContext.Consumer>
              {tabletData =>
                tabletData && (
                  <BlockchainProfile
                    client={client}
                    loggedCustomer={loggedCustomer}
                    hasNoInterests={tabletData.hasNoInterests}
                    {...props}
                  />
                )
              }
            </TabletDataContext.Consumer>
          )
        }
      </CustomerDataContext.Consumer>
    )}
  </ApolloConsumer>
);



// WEBPACK FOOTER //
// ./src/screens/Main/edit-profile/blockchain/blockchainProfile.tsx